/**
 * Chat state — zone buffer + `/world` broadcast buffer + outbound compose.
 *
 * Feed points:
 *   - `WorldEvent::Spoke` → `appendZoneMessage`. Zone scoping is already
 *     enforced by the gateway (CHAT_SYSTEM §3), so every `Spoke` that
 *     reaches this client is in the local player's zone.
 *   - `ServerMessage::ZoneBufferSnapshot` → `replaceZoneBuffer`. Server-
 *     authoritative per Q-C6: the client never clears speculatively.
 *   - `EventEnvelope::WorldChat` → `appendWorldBroadcast`.
 *   - `WorldEvent::ZoneSystemMessage` → `appendSystemMessage`.
 *
 * Buffers are bounded: zone holds 200 messages (CHAT_SYSTEM §4.3 indoor
 * cap), `/world` holds 200. System messages share the zone tab.
 */

import { create } from 'zustand';

import type { EntityId } from '../wire';

const ZONE_BUFFER_MAX = 200;
const WORLD_BUFFER_MAX = 200;

export type ChatTab = 'zone' | 'world';

export interface ChatMessage {
  /** Client-local id — monotonic sequence. */
  id: number;
  /** Which tab owns the message. */
  tab: ChatTab;
  /** Kind: speaker-attributed (`utterance`), broadcast (`broadcast`), or
   *  zone-system (`system`). Drives rendering treatment. */
  kind: 'utterance' | 'broadcast' | 'system';
  /** Speaker entity for `utterance`, `null` for system + broadcast. */
  speaker: EntityId | null;
  /** Display text. For utterances this is the spoken text verbatim;
   *  markdown is Phase-1 subset (bold / italic / inline-code) applied
   *  only in the pane, never in the speech bubble. */
  text: string;
  /** Server tick at send time. Used to order messages + age bubbles. */
  tick: bigint;
}

export interface ChatState {
  activeTab: ChatTab;
  zone: ChatMessage[];
  world: ChatMessage[];
  /** Compose-input draft, persisted across tab swaps. */
  composeText: string;
  nextId: number;

  // ─── Actions ─────────────────────────────────────────────────────────
  setActiveTab: (tab: ChatTab) => void;
  setComposeText: (text: string) => void;

  appendZoneMessage: (msg: Omit<ChatMessage, 'id' | 'tab'>) => void;
  appendWorldBroadcast: (msg: Omit<ChatMessage, 'id' | 'tab'>) => void;
  appendSystemMessage: (msg: Omit<ChatMessage, 'id' | 'tab' | 'kind' | 'speaker'>) => void;
  replaceZoneBuffer: (messages: Array<Omit<ChatMessage, 'id' | 'tab'>>) => void;

  reset: () => void;
}

export const useChat = create<ChatState>((set) => ({
  activeTab: 'zone',
  zone: [],
  world: [],
  composeText: '',
  nextId: 1,

  setActiveTab: (tab) => set(() => ({ activeTab: tab })),
  setComposeText: (text) => set(() => ({ composeText: text })),

  appendZoneMessage: (msg) =>
    set((s) => ({
      zone: cap([...s.zone, { ...msg, id: s.nextId, tab: 'zone' }], ZONE_BUFFER_MAX),
      nextId: s.nextId + 1,
    })),

  appendWorldBroadcast: (msg) =>
    set((s) => ({
      world: cap(
        [...s.world, { ...msg, id: s.nextId, tab: 'world' }],
        WORLD_BUFFER_MAX,
      ),
      nextId: s.nextId + 1,
    })),

  appendSystemMessage: (msg) =>
    set((s) => ({
      zone: cap(
        [
          ...s.zone,
          {
            ...msg,
            id: s.nextId,
            tab: 'zone',
            kind: 'system',
            speaker: null,
          },
        ],
        ZONE_BUFFER_MAX,
      ),
      nextId: s.nextId + 1,
    })),

  replaceZoneBuffer: (messages) =>
    set((s) => {
      let counter = s.nextId;
      const rebuilt: ChatMessage[] = messages.map((m) => ({
        ...m,
        id: counter++,
        tab: 'zone',
      }));
      return {
        zone: cap(rebuilt, ZONE_BUFFER_MAX),
        nextId: counter,
      };
    }),

  reset: () =>
    set(() => ({
      activeTab: 'zone',
      zone: [],
      world: [],
      composeText: '',
      nextId: 1,
    })),
}));

function cap<T>(arr: T[], max: number): T[] {
  if (arr.length <= max) return arr;
  return arr.slice(arr.length - max);
}
